import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  createDifyKnowledgeDocument,
  deleteDifyKnowledgeDocument,
  getDifyKnowledgeDocument,
  listDifyKnowledgeDocuments,
  updateDifyKnowledgeDocument,
} from '../../api/difyKnowledge';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Loading } from '../../components/Loading';
import { TextField } from '../../components/TextField';
import { aiKnowledgeStrings, commonStrings, strings } from '../../constants';
import { DocumentsTable } from './DocumentsTable';
import { DocumentsCardList } from './DocumentsCardList';

const PAGE_SIZE = 20;

export default function AiKnowledgePage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [page, setPage] = useState(1);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(
    null,
  );

  const isEditing = editingDocumentId !== null;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dify-knowledge-documents', page],
    queryFn: () => listDifyKnowledgeDocuments({ page, limit: PAGE_SIZE }),
  });

  const total = data?.total ?? 0;
  const totalPage = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0;

  const countLabel = useMemo(
    () => `${total}${commonStrings.countSuffix}`,
    [total],
  );
  const pageLabel = useMemo(
    () => (totalPage > 0 ? `${page} / ${totalPage}` : '0 / 0'),
    [page, totalPage],
  );
  const canMovePrev = useMemo(
    () => !isLoading && totalPage > 0 && page > 1,
    [isLoading, totalPage, page],
  );
  const canMoveNext = useMemo(
    () => !isLoading && totalPage > 0 && page < totalPage,
    [isLoading, totalPage, page],
  );

  const resetForm = useCallback(() => {
    setEditingDocumentId(null);
    setName('');
    setText('');
  }, []);

  const loadDocumentMutation = useMutation({
    mutationFn: (documentId: string) => getDifyKnowledgeDocument(documentId),
    onSuccess: (doc) => {
      setEditingDocumentId(doc.id);
      setName(doc.name);
      setText(doc.text);
    },
    onError: () => {
      toast.error(commonStrings.somethingWentWrong);
    },
  });

  const createMutation = useMutation({
    mutationFn: () => createDifyKnowledgeDocument({ name: name.trim(), text }),
    onSuccess: () => {
      toast.success(aiKnowledgeStrings.addSuccess);
      resetForm();
      void queryClient.invalidateQueries({
        queryKey: ['dify-knowledge-documents'],
      });
    },
    onError: () => {
      toast.error(commonStrings.somethingWentWrong);
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateDifyKnowledgeDocument(editingDocumentId!, {
        name: name.trim(),
        text,
      }),
    onSuccess: () => {
      toast.success(aiKnowledgeStrings.updateSuccess);
      void queryClient.invalidateQueries({
        queryKey: ['dify-knowledge-documents'],
      });
    },
    onError: () => {
      toast.error(commonStrings.somethingWentWrong);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => deleteDifyKnowledgeDocument(documentId),
    onSuccess: (_data, documentId) => {
      toast.success(aiKnowledgeStrings.deleteSuccess);
      if (editingDocumentId === documentId) {
        resetForm();
      }
      void refetch();
    },
    onError: () => {
      toast.error(commonStrings.somethingWentWrong);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) return;
    if (isEditing) {
      updateMutation.mutate();
      return;
    }
    createMutation.mutate();
  };

  const isFormPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    loadDocumentMutation.isPending;

  return (
    <AppLayout>
      <section
        className="flex h-auto lg:h-[calc(100vh-10rem)] w-full flex-col overflow-hidden rounded-[28px] border
       border-white/70 bg-white/85 shadow-soft backdrop-blur-sm"
      >
        <header className="flex-shrink-0 border-b border-neutral-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-ink">
            {aiKnowledgeStrings.title}
          </h1>
          <p className="mt-2 text-sm text-muted">
            {aiKnowledgeStrings.description}
          </p>
        </header>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <form
            id="ai-knowledge-form"
            onSubmit={handleSubmit}
            className="flex w-full lg:w-1/2 min-w-0 flex-col gap-4 lg:overflow-y-auto border-b lg:border-b-0 lg:border-r border-neutral-200 p-6"
          >
            <h2 className="text-lg font-semibold text-ink">
              {isEditing
                ? aiKnowledgeStrings.editDocument
                : aiKnowledgeStrings.addDocument}
            </h2>
            {loadDocumentMutation.isPending ? (
              <div className="flex justify-center py-12">
                <Loading />
              </div>
            ) : (
              <>
                <TextField
                  label={aiKnowledgeStrings.documentName}
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <TextField
                  label={aiKnowledgeStrings.documentContent}
                  name="text"
                  type="textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={12}
                  inputClass="min-h-[280px] flex-1 resize-y"
                />
                <div className="flex flex-shrink-0 flex-wrap gap-2">
                  <Button
                    type="submit"
                    disabled={isFormPending || !name.trim() || !text.trim()}
                  >
                    {isEditing
                      ? aiKnowledgeStrings.updateDocument
                      : aiKnowledgeStrings.addDocument}
                  </Button>
                  {isEditing && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isFormPending}
                      onClick={resetForm}
                    >
                      {aiKnowledgeStrings.cancelEdit}
                    </Button>
                  )}
                </div>
              </>
            )}
          </form>

          <div className="flex w-full lg:w-1/2 min-w-0 flex-col overflow-hidden p-6">
            <header className="mb-4 flex flex-shrink-0 flex-wrap items-start justify-between gap-4">
              <div className="grid gap-1">
                <h2 className="text-lg font-semibold text-ink">
                  {aiKnowledgeStrings.listTitle}
                </h2>
                <p className="text-xs text-muted">
                  {aiKnowledgeStrings.selectDocumentHint}
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
                  {countLabel}
                </span>
                {totalPage > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canMovePrev}
                      onClick={() => {
                        if (page > 1) {
                          setPage((prev) => Math.max(1, prev - 1));
                        }
                      }}
                    >
                      {strings.common.pagination.previous}
                    </Button>
                    <span className="min-w-[86px] text-center text-xs font-semibold text-muted">
                      {pageLabel} ページ
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canMoveNext}
                      onClick={() => {
                        if (page < totalPage) {
                          setPage((prev) => prev + 1);
                        }
                      }}
                    >
                      {strings.common.pagination.next}
                    </Button>
                  </div>
                )}
              </div>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
                <DocumentsTable
                  documents={data?.data ?? []}
                  loading={isLoading}
                  selectedDocumentId={editingDocumentId}
                  deletePending={deleteMutation.isPending}
                  onSelect={(documentId) =>
                    loadDocumentMutation.mutate(documentId)
                  }
                  onDelete={(documentId) => deleteMutation.mutate(documentId)}
                />
              </div>
              <div className="lg:hidden">
                <DocumentsCardList
                  documents={data?.data ?? []}
                  loading={isLoading}
                  selectedDocumentId={editingDocumentId}
                  deletePending={deleteMutation.isPending}
                  onSelect={(documentId) => {
                    loadDocumentMutation.mutate(documentId)
                    document
                      .getElementById('ai-knowledge-form')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  onDelete={(documentId) => deleteMutation.mutate(documentId)}
                  className="md:grid-cols-2"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </AppLayout>
  );
}
